import React, { useState } from "react";
import {
  IonCard,
  IonCardContent,
  IonIcon,
  IonButton,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonToast,
} from "@ionic/react";
import {
  footballOutline,
  trophyOutline,
  podiumOutline,
} from "ionicons/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuthStore } from "@/store/authStore";
import { useHistory } from "react-router-dom";
import { useIonRouter } from "@ionic/react";
import { useI18n } from "@/i18n";
import api from "@/services/api";

const Home: React.FC = () => {
  const { player, updatePlayer } = useAuthStore();
  const history = useHistory();
  const ionRouter = useIonRouter();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState({ open: false, message: "", color: "success" });

  const { data: seasonsData } = useQuery(
    ["seasons-home"],
    async () => (await api.get("/api/leagues/seasons")).data,
    { staleTime: 60000 }
  );

  const activeSeason = seasonsData?.seasons?.find((s: any) => s.is_active);
  const latestSeason = seasonsData?.seasons?.[0];
  const displaySeason = activeSeason || latestSeason;
  const isSeasonActive = Boolean(displaySeason?.is_active);
  const needsJoin = Boolean(isSeasonActive && activeSeason && !activeSeason.player_joined);

  const joinSeason = useMutation(
    async () => (await api.post("/api/leagues/join-season")).data,
    {
      onSuccess: (data) => {
        if (player) {
          updatePlayer({
            ...player,
            currentLeagueId: data.league_id,
            totalPoints: 0,
          });
        }
        queryClient.invalidateQueries(["seasons-home"]);
        queryClient.invalidateQueries(["my-league"]);
        queryClient.invalidateQueries(["all-leagues"]);
        setToast({
          open: true,
          message: `${t("joinSeasonSuccess")} ${data.league_name}`,
          color: "success",
        });
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.error === "Already joined this season"
            ? t("alreadyInSeason")
            : t("joinSeasonError");
        setToast({ open: true, message, color: "danger" });
      },
    }
  );

  const navigate = (path: string) => {
    ionRouter.push(path);
    setTimeout(() => history.push(path), 50);
    setTimeout(() => {
      if (window.location.pathname !== path) window.location.href = path;
    }, 200);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("appTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: "16px" }}>
          {/* Greeting bar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}>
            <div>
              <h2 style={{ margin: 0, fontWeight: 700, fontSize: "20px" }}>
                {t("welcomeUser")}, {player?.displayName || player?.username || "User"}
              </h2>
              <p style={{ margin: "4px 0 0", color: "var(--ion-color-medium)", fontSize: "14px" }}>
                {t("homeIntro")}
              </p>
              <IonButton
                fill="clear"
                size="small"
                routerLink="/how-it-works"
                style={{ "--padding-start": "0", marginTop: "4px" }}
              >
                {t("howItWorksNav")}
              </IonButton>
            </div>
            {player?.totalPoints !== undefined && (
              <div className="stat-pill" style={{ background: "var(--app-gold)", minWidth: "60px" }}>
                <strong style={{ color: "#000" }}>{player.totalPoints}</strong>
                <span style={{ color: "rgba(0,0,0,0.6)" }}>{t("points")}</span>
              </div>
            )}
          </div>

          {needsJoin && (
            <IonCard style={{ marginBottom: "12px" }}>
              <IonCardContent style={{ padding: "16px" }}>
                <strong style={{ fontSize: "16px" }}>{t("joinNewSeason")}</strong>
                <p style={{ margin: "6px 0 12px", color: "var(--ion-color-medium)", fontSize: "13px" }}>
                  {t("joinNewSeasonBody")}
                </p>
                <IonButton
                  expand="block"
                  className="save-btn"
                  style={{ "--border-radius": "10px" }}
                  disabled={joinSeason.isLoading}
                  onClick={() => joinSeason.mutate()}
                >
                  {joinSeason.isLoading
                    ? t("joiningSeason")
                    : `${t("joinSeasonButton")} ${activeSeason.name}`}
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* Quick actions */}
          <IonCard style={{ marginBottom: "12px" }}>
            <IonCardContent style={{ padding: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <IonIcon icon={footballOutline} style={{ fontSize: "28px", color: "var(--ion-color-primary)" }} />
                <div>
                  <strong style={{ fontSize: "16px" }}>{t("nextMatchday")}</strong>
                  <p style={{ margin: "2px 0 0", color: "var(--ion-color-medium)", fontSize: "13px" }}>
                    {t("matchdayInfoLoading")}
                  </p>
                </div>
              </div>
              <IonButton
                expand="block"
                className="save-btn"
                style={{ "--border-radius": "10px" }}
                onClick={() => navigate("/matches")}
              >
                {t("makePredictions")}
              </IonButton>
            </IonCardContent>
          </IonCard>

          {/* Stat pills row */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
            <div
              className="stat-pill"
              style={{ flex: 1, cursor: "pointer" }}
              onClick={() => navigate("/leaderboard")}
            >
              <IonIcon icon={podiumOutline} style={{ fontSize: "20px", color: "var(--ion-color-primary)", marginBottom: "4px" }} />
              <strong>{player?.totalPoints || 0}</strong>
              <span>{t("points")}</span>
            </div>
            <div
              className="stat-pill"
              style={{ flex: 1, cursor: "pointer" }}
              onClick={() => navigate("/leaderboard")}
            >
              <IonIcon icon={trophyOutline} style={{ fontSize: "20px", color: "var(--app-gold)", marginBottom: "4px" }} />
              <strong>–</strong>
              <span>{t("leaderboard")}</span>
            </div>
            <div
              className="stat-pill"
              style={{ flex: 1, cursor: "pointer" }}
              onClick={() => navigate("/predictions")}
            >
              <IonIcon icon={footballOutline} style={{ fontSize: "20px", color: "var(--ion-color-success)", marginBottom: "4px" }} />
              <strong>–</strong>
              <span>{t("predictions")}</span>
            </div>
          </div>

          {/* Season info */}
          {displaySeason && (
            <IonCard>
              <IonCardContent style={{ padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <strong style={{ fontSize: "14px" }}>{t("seasonStatus")}</strong>
                    <p style={{ margin: "2px 0 0", color: "var(--ion-color-medium)", fontSize: "13px" }}>
                      Bundesliga {displaySeason.name}
                    </p>
                  </div>
                  <span style={{
                    background: isSeasonActive ? "var(--app-score-diff)" : "var(--ion-color-medium)",
                    color: "#fff",
                    borderRadius: "6px",
                    padding: "3px 10px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}>
                    {isSeasonActive ? t("active") : t("completed")}
                  </span>
                </div>
              </IonCardContent>
            </IonCard>
          )}
        </div>
        <IonToast
          isOpen={toast.open}
          message={toast.message}
          color={toast.color}
          duration={3000}
          onDidDismiss={() => setToast((current) => ({ ...current, open: false }))}
        />
      </IonContent>
    </IonPage>
  );
};

export default Home;
